import React, { useState, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { signOut } from '../lib/supabase'
import { getNavForRole } from '../lib/navConfig'

export default function Sidebar() {
  const { profile, userRole } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const navRef = useRef(null)

  const nav = getNavForRole(userRole)

  // Which section contains the current route
  const activeSectionKey = nav.find(s =>
    s.items.some(i => location.pathname === i.to || location.pathname.startsWith(i.to + '/'))
  )?.key

  // Open state — sections marked defaultOpen start open, active section always open
  const [openSections, setOpenSections] = useState(() => {
    const open = {}
    nav.forEach(s => {
      open[s.key] = s.defaultOpen || s.key === activeSectionKey
    })
    if (!activeSectionKey && nav.length > 0) open[nav[0].key] = true
    return open
  })

  // Toggle a section — accordion: clicking closed opens it (closes others), clicking open closes it
  // Scroll position is preserved because we never touch navRef.scrollTop here
  const toggleSection = (key) => {
    setOpenSections(prev => {
      const isOpen = prev[key]
      const next = {}
      nav.forEach(s => { next[s.key] = false }) // close all
      if (!isOpen) next[key] = true              // open clicked if it was closed
      return next
    })
  }

  // When navigating to a page, ensure its section is open without touching others
  // This is synchronous during render — no useEffect needed, no competing side effects
  const ensuredSections = { ...openSections }
  if (activeSectionKey && !ensuredSections[activeSectionKey]) {
    ensuredSections[activeSectionKey] = true
  }

  const handleSignOut = async () => { await signOut(); navigate('/login') }

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'

  const roleBadge = {
    finance_admin: { label: 'Finance Admin', bg: 'var(--amber)', color: 'var(--green-deep)' },
    ops_admin:     { label: 'Operations',    bg: 'var(--green-bright)', color: 'white' },
    staff:         { label: 'Staff',         bg: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' },
  }[userRole] || { label: 'User', bg: 'rgba(255,255,255,0.1)', color: 'white' }

  return (
    <div className="sidebar">
      <div className="sidebar-brand">
        <img src="/logo.png" alt="Kobflo" onError={e => { e.target.style.display = 'none' }} />
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '0.88rem', fontWeight: 900, color: 'var(--green-deep)', textAlign: 'center', marginTop: 4, lineHeight: 1.2 }}>
          Kobflo Greentek Ltd
        </div>
      </div>

      {profile && (
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{profile.full_name || profile.email}</div>
            <span style={{ fontSize: '0.58rem', fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em', textTransform: 'uppercase', background: roleBadge.bg, color: roleBadge.color, padding: '2px 6px', borderRadius: 3, marginTop: 3, display: 'inline-block' }}>
              {roleBadge.label}
            </span>
          </div>
        </div>
      )}

      <nav className="sidebar-nav" ref={navRef}>
        {nav.map(section => {
          const isOpen = ensuredSections[section.key]
          const isActiveSection = section.key === activeSectionKey

          return (
            <div key={section.key} className="nav-section">
              <button
                onClick={() => toggleSection(section.key)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: isActiveSection ? 'rgba(255,255,255,0.07)' : 'none',
                  border: 'none',
                  borderLeft: isActiveSection ? '3px solid var(--amber)' : '3px solid transparent',
                  cursor: 'pointer',
                  padding: '9px 14px 9px 13px',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '0.9rem', minWidth: 18, textAlign: 'center' }}>{section.icon}</span>
                <span style={{
                  flex: 1,
                  fontSize: '0.68rem',
                  fontFamily: "'DM Mono',monospace",
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  fontWeight: 700,
                  color: isActiveSection ? 'var(--amber)' : 'rgba(255,255,255,0.45)',
                }}>
                  {section.label}
                </span>
                <span style={{
                  fontSize: '0.5rem',
                  color: 'rgba(255,255,255,0.25)',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}>▼</span>
              </button>

              {isOpen && (
                <div style={{ paddingBottom: 6 }}>
                  {section.items.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                      style={{ paddingLeft: 38 }}
                    >
                      <span className="nav-icon" style={{ fontSize: '0.82rem' }}>{item.icon}</span>
                      <span style={{ fontSize: '0.82rem' }}>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      <div className="sidebar-footer">
        <button
          onClick={handleSignOut}
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: '0.75rem', width: '100%', fontFamily: 'inherit' }}
        >
          🚪 Sign Out
        </button>
        <div style={{ marginTop: 8, fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)', textAlign: 'center', fontFamily: "'DM Mono',monospace" }}>
          app.kobfloagri.com
        </div>
      </div>
    </div>
  )
}
